import { state } from "../state.js";
import type { Renderer } from "./Renderer.js";
import { BEAM_MS, EXPLOSION_MS, type Beam, type FogView } from "./types.js";
import { isPointVisible } from "./fog.js";

export function drawBeams(rd: Renderer, nowMs: number, fog: FogView | null): void {
  const { ctx } = rd;
  rd.beams = rd.beams.filter((b) => nowMs - b.start < BEAM_MS);
  ctx.lineCap = "round";
  for (const b of rd.beams) {
    if (fog && !isPointVisible((b.x1 + b.x2) / 2, (b.y1 + b.y2) / 2, fog)) continue;
    const alpha = 1 - (nowMs - b.start) / BEAM_MS;
    // Layered beam: violet glow → blue → white core.
    ctx.globalAlpha = alpha * 0.3;
    ctx.strokeStyle = "#9b3fd6";
    ctx.lineWidth = 8;
    strokeLine(rd, b);
    ctx.globalAlpha = alpha * 0.6;
    ctx.strokeStyle = "#6aa6ff";
    ctx.lineWidth = 4;
    strokeLine(rd, b);
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 1.8;
    strokeLine(rd, b);
  }
  ctx.globalAlpha = 1;
}

function strokeLine(rd: Renderer, b: Beam): void {
  const { ctx } = rd;
  ctx.beginPath();
  ctx.moveTo(b.x1, b.y1);
  ctx.lineTo(b.x2, b.y2);
  ctx.stroke();
}

export function drawExplosions(rd: Renderer, nowMs: number, fog: FogView | null): void {
  const { ctx } = rd;
  rd.explosions = rd.explosions.filter((e) => nowMs - e.start < EXPLOSION_MS);
  for (const e of rd.explosions) {
    if (fog && !isPointVisible(e.x, e.y, fog)) continue;
    const age = (nowMs - e.start) / EXPLOSION_MS; // 0..1
    ctx.save();
    ctx.translate(e.x, e.y);

    // White-hot core that cools to orange as it fades.
    const coreR = 7 + age * 18;
    const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, coreR);
    if (state.modernEnabled) {
      grad.addColorStop(0, age < 0.3 ? "#ffffff" : "#00f8f8");
      grad.addColorStop(0.55, age < 0.4 ? "#00f8f8" : "#1e40af");
      grad.addColorStop(1, "rgba(30,64,175,0)");
    } else {
      grad.addColorStop(0, age < 0.3 ? "#fff6d8" : "#ffd24a");
      grad.addColorStop(0.55, age < 0.4 ? "#ffd24a" : "#e6863f");
      grad.addColorStop(1, "rgba(230,134,63,0)");
    }
    ctx.globalAlpha = (1 - age) * 0.95;
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(0, 0, coreR, 0, Math.PI * 2);
    ctx.fill();

    // Expanding shock ring + a faster, fainter outer pressure wave.
    ctx.globalAlpha = 1 - age;
    ctx.beginPath();
    ctx.arc(0, 0, 8 + age * 30, 0, Math.PI * 2);
    ctx.strokeStyle = state.modernEnabled ? "#00f8f8" : "#b23b2e";
    ctx.lineWidth = 3 * (1 - age) + 0.5;
    ctx.stroke();
    ctx.globalAlpha = (1 - age) * 0.35;
    ctx.beginPath();
    ctx.arc(0, 0, 10 + age * 46, 0, Math.PI * 2);
    ctx.strokeStyle = state.modernEnabled ? "#1e293b" : "#e6d6b8";
    ctx.lineWidth = 1.5 * (1 - age) + 0.5;
    ctx.stroke();

    // Sparks flung outward (deterministic by index, no RNG needed).
    ctx.globalAlpha = (1 - age) * 0.85;
    ctx.fillStyle = e.color;
    const sparks = 8;
    const dist = 6 + age * 34;
    for (let i = 0; i < sparks; i++) {
      const a = (i / sparks) * Math.PI * 2 + i;
      ctx.beginPath();
      ctx.arc(Math.cos(a) * dist, Math.sin(a) * dist, 2.2 * (1 - age) + 0.5, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
  ctx.globalAlpha = 1;
}
